import {
  Button,
  chakra,
  Flex,
  FlexProps,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiEmotionSadLine } from "@remixicon/react";
import { ReactNode, useCallback } from "react";

import { CodeExampleUsecases } from "@/feature/components/FeatureCodeExample";
import { useSetupInstructions } from "@/feature/components/SetupInstructions";

const ErrorIcon = chakra(RiEmotionSadLine);

export type MonitorLoadingStateProps = FlexProps;

export function MonitorLoadingState(props: MonitorLoadingStateProps) {
  return (
    <Flex align="center" justify="center" {...props}>
      <Spinner color="dimmed" />
    </Flex>
  );
}

export type MonitorErrorStateProps = FlexProps & {
  usecase: CodeExampleUsecases | "events";
  onRetry?: () => void;
};

export function MonitorErrorState({
  usecase,
  onRetry,
  ...rest
}: MonitorErrorStateProps) {
  const errorColor = useColorModeValue("red.500", "red.300");

  return (
    <Flex align="center" direction="column" gap={4} justify="center" {...rest}>
      <ErrorIcon size={24} />
      <Text color={errorColor}>Failed to load {usecase} data</Text>
      <Button variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </Flex>
  );
}

export type MonitorEmptyDataStateProps = FlexProps & {
  children?: ReactNode;
  usecase: CodeExampleUsecases;
  title?: string;
  noDataText?: string;
};

export function MonitorEmptyDataState({
  usecase,
  title,
  noDataText,
  ...rest
}: MonitorEmptyDataStateProps) {
  return (
    <Flex direction="column" position="relative" {...rest}>
      <Flex
        align="center"
        bg="popoverBackground"
        borderRadius="md"
        direction="column"
        gap={4}
        h="full"
        justify="center"
        left={0}
        position="absolute"
        top={0}
        w="full"
      >
        <Text fontSize="md" fontWeight="medium">
          {noDataText ?? `No ${usecase} yet`}
        </Text>
        <SetupInstructionsButton title={title ?? `Setup ${usecase}`} />
      </Flex>
    </Flex>
  );
}

export type EmptyMonitorStateProps = FlexProps & {
  children?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  hasData?: boolean;
  usecase: CodeExampleUsecases;
  noDataText?: string;
  title?: string;
  onRetry?: () => void;
};

export function EmptyMonitorState({
  children,
  isLoading,
  isError,
  hasData,
  usecase,
  onRetry,
  ...rest
}: EmptyMonitorStateProps) {
  if (isLoading) {
    return <MonitorLoadingState {...rest} />;
  }

  if (isError) {
    return <MonitorErrorState usecase={usecase} onRetry={onRetry} {...rest} />;
  }

  if (!hasData) {
    return (
      <MonitorEmptyDataState usecase={usecase} {...rest}>
        {children}
      </MonitorEmptyDataState>
    );
  }

  return children;
}

export function SetupInstructionsButton({
  title = "Code Example",
}: {
  title?: string;
}) {
  const [isVisible, setIsVisible] = useSetupInstructions();

  const onClick = useCallback(() => {
    setIsVisible(true);

    setTimeout(
      () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
      // if the instructions are not visible, add a tiny timout to allow for the instructions box to appear in the DOM
      isVisible ? 0 : 10,
    );
  }, [isVisible, setIsVisible]);

  return (
    <Button variant="outline" onClick={onClick}>
      {title}
    </Button>
  );
}
